// With no PIN on the web, an unattended session is closed instead: after IDLE_MS without input the
// user is signed out. The last activity time is kept in localStorage, so a tab that was closed or
// asleep longer than that also signs out when it opens again.
import {useEffect} from 'react';

const IDLE_MS = 15 * 60 * 1000;
const KEY = 'web:lastActivity';
const EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

const readLast = () => {
  try {
    return Number(localStorage.getItem(KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeLast = (time: number) => {
  try {
    localStorage.setItem(KEY, String(time));
  } catch {
    // Storage blocked — the in-memory timer still works.
  }
};

export function useIdleLogout(isAuthenticated: boolean, logout: () => Promise<void>) {
  useEffect(() => {
    // Jest renders App in a React Native environment, which has no DOM.
    if (!isAuthenticated || typeof document === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let done = false;

    const expire = () => {
      if (done) return;
      done = true;
      try {
        localStorage.removeItem(KEY);
      } catch {
        // ignore
      }
      logout();
    };

    const schedule = () => {
      clearTimeout(timer);
      const left = readLast() + IDLE_MS - Date.now();
      if (left <= 0) expire();
      else timer = setTimeout(schedule, left);
    };

    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite < 5000) return; // other tabs only need the time to the second or so
      lastWrite = now;
      writeLast(now);
      schedule();
    };

    // Timers are throttled in hidden tabs; check again when the tab comes back.
    const onVisible = () => {
      if (document.visibilityState === 'visible') schedule();
    };

    // A session restored after a long pause expires right away; a fresh one starts the clock.
    const last = readLast();
    if (last && Date.now() - last >= IDLE_MS) {
      expire();
      return;
    }
    writeLast(Date.now());
    schedule();

    EVENTS.forEach(e => window.addEventListener(e, onActivity, {passive: true}));
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      EVENTS.forEach(e => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, logout]);
}
